import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { log } from "../../lib/log";
import { messagesUrl } from "./chatUtils";

const EMPTY_MENTIONS = { total: 0, by_group: {} };

// Sidebar lists + unread mention badges (polled every 8s).
export function useChatLists(kind, id) {
  const [hubs, setHubs] = useState([]);
  const [dms, setDms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [mentions, setMentions] = useState(EMPTY_MENTIONS);

  useEffect(() => {
    (async () => {
      try {
        const [h, d, g, m] = await Promise.all([
          api.get("/hubs"),
          api.get("/dms"),
          api.get("/groups"),
          api.get("/mentions/unread").catch(() => ({ data: EMPTY_MENTIONS })),
        ]);
        setHubs(h.data);
        setDms(d.data);
        setGroups(g.data);
        setMentions(m.data);
      } catch (e) {
        log.warn("Chat: initial fetch failed:", e);
      }
    })();
  }, []);

  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const m = await api.get("/mentions/unread");
        setMentions(m.data);
      } catch (e) {
        log.debug("Chat: mentions poll transient error:", e?.response?.status || e?.message);
      }
    }, 8000);
    return () => clearInterval(t);
  }, []);

  // Opening a group clears that group's mention badge.
  useEffect(() => {
    if (kind !== "group" || !id) return;
    api.post("/mentions/clear", { group_id: id }).then(() => {
      setMentions((m) => {
        const { [id]: _drop, ...rest } = m.by_group || {};
        return { total: Object.values(rest).reduce((a, b) => a + b, 0), by_group: rest };
      });
    }).catch((e) => log.debug("Chat: mentions clear failed:", e?.response?.status || e?.message));
  }, [kind, id]);

  return { hubs, dms, groups, setGroups, mentions };
}

// Active channel messages, polled every 3s.
export function useChannelMessages(kind, id) {
  const [messages, setMessages] = useState([]);
  useEffect(() => {
    if (!kind || !id) { setMessages([]); return; }
    let live = true;
    const load = async () => {
      try {
        const res = await api.get(messagesUrl(kind, id));
        if (live) setMessages(res.data);
      } catch (e) {
        log.debug("Chat poll transient error:", e?.response?.status || e?.message);
      }
    };
    load();
    const t = setInterval(load, 3000);
    return () => { live = false; clearInterval(t); };
  }, [kind, id]);
  return [messages, setMessages];
}
